/**
 * dexScanTargets — the gap SOURCE for the live-scan PiP guidance.
 *
 * Answers "which dex numbers are still missing for this variant (in the region
 * being scanned), and how many are caught vs total" using the SAME roster logic
 * the Pokédex grid uses (`computeDexGridNumbers`), so a shiny/lucky/perfect gap
 * list matches exactly what the user sees in Nexus Dex — curated rosters, sparse
 * variants and all. Kept separate from the pure `dexScanGuidance` brain so the
 * store/roster dependency lives here and the guidance stays trivially testable.
 */
import { computeDexGridNumbers, REGION_FILTER_ORDER, type RegionFilter } from "./dexGridFilter";
import { listDexEntriesForVariant } from "./dexStateStore";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

export interface ScanTargets {
  /** Ascending dex numbers still missing for the variant, region-scoped. */
  missingIds: number[];
  /** Caught count within the same scope. */
  caught: number;
  /** Roster total within the same scope (caught + missing). */
  total: number;
}

/** Map the scan's free-string region ("kanto", "unknown", …) to a RegionFilter. */
function toRegionFilter(raw: string | null | undefined): RegionFilter {
  if (raw && (REGION_FILTER_ORDER as string[]).includes(raw)) return raw as RegionFilter;
  return "all";
}

/**
 * Compute the missing/caught/total for a variant, scoped to `regionRaw` when it
 * is a recognised region (else whole variant). Reads the dex store.
 */
export function scanTargetsForVariant(
  variant: PokedexVariant,
  regionRaw?: string | null,
): ScanTargets {
  const recorded = listDexEntriesForVariant(variant).map((e) => ({
    dexNumber: e.dexNumber,
    state: e.state,
    source: e.source,
  }));
  const allIds = computeDexGridNumbers({
    variant,
    recorded,
    region: toRegionFilter(regionRaw),
    status: "all",
    search: "",
    sort: "dex-asc",
    types: [],
  });
  const presentSet = new Set(
    recorded.filter((e) => e.state === "present").map((e) => e.dexNumber),
  );
  const missingIds = allIds.filter((id) => !presentSet.has(id)).sort((a, b) => a - b);
  return { missingIds, caught: allIds.length - missingIds.length, total: allIds.length };
}
